import { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { deleteNote, updateNote, type Note } from "@/lib/composerNotes";

type Props = {
  note: Note | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void;
};

export const NoteEditorSheet = ({ note, open, onOpenChange, onChanged }: Props) => {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [savingState, setSavingState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const lastSentRef = useRef<{ title: string; body: string } | null>(null);

  // Hydrate fields when a new note opens.
  useEffect(() => {
    if (note && open) {
      setTitle(note.title ?? "");
      setBody(note.body ?? "");
      lastSentRef.current = { title: note.title ?? "", body: note.body ?? "" };
      setSavingState("idle");
    }
  }, [note, open]);

  const persist = async (nextTitle: string, nextBody: string) => {
    if (!note) return;
    const last = lastSentRef.current;
    if (last && last.title === nextTitle && last.body === nextBody) return;
    setSavingState("saving");
    try {
      await updateNote(note.id, { title: nextTitle, body: nextBody });
      lastSentRef.current = { title: nextTitle, body: nextBody };
      setSavingState("saved");
      onChanged();
    } catch (err) {
      console.error("[NoteEditorSheet] save failed", err);
      setSavingState("error");
      toast({ title: "Couldn't save note", description: (err as Error).message, variant: "destructive" });
    }
  };

  // Debounced auto-save while typing.
  useEffect(() => {
    if (!open || !note) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void persist(title, body);
    }, 700);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, open, note?.id]);

  // Final flush when the sheet closes. If the note was empty on open AND
  // is still empty on close, delete it instead of persisting an empty row.
  const handleOpenChange = (next: boolean) => {
    if (!next && note) {
      const openedEmpty = !note.title?.trim() && !note.body?.trim();
      const stillEmpty = !title.trim() && !body.trim();
      if (openedEmpty && stillEmpty) {
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        deleteNote(note.id)
          .then(() => onChanged())
          .catch((err) => console.error("[NoteEditorSheet] cleanup delete failed", err));
      } else {
        void persist(title, body);
      }
    }
    onOpenChange(next);
  };

  const handleDelete = async () => {
    if (!note) return;
    try {
      await deleteNote(note.id);
      toast({ title: "Note deleted" });
      setConfirmDelete(false);
      onOpenChange(false);
      onChanged();
    } catch (err) {
      toast({ title: "Couldn't delete", description: (err as Error).message, variant: "destructive" });
    }
  };

  const statusLabel =
    savingState === "saving" ? "Saving…" :
    savingState === "saved" ? "Saved" :
    savingState === "error" ? "Save failed" : "";

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="flex w-full flex-col gap-3 sm:max-w-xl">
          <SheetHeader className="space-y-1">
            <SheetTitle>Edit note</SheetTitle>
            <SheetDescription className="text-xs">
              Auto-saves as you type. {statusLabel && <span className="ml-1 text-muted-foreground">{statusLabel}</span>}
            </SheetDescription>
          </SheetHeader>
          <Input
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-base"
            // Allow IME composition / non-Latin input identical to other surfaces.
            spellCheck
            autoComplete="off"
          />
          <Textarea
            placeholder="Start typing…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="min-h-[55vh] flex-1 resize-none font-normal leading-relaxed"
            spellCheck
            autoComplete="off"
          />
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Delete
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={() => handleOpenChange(false)}>
              Done
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
